import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { PageDto, PageQueriesModel } from './../shared';
import { ProductLineDto } from './../product-line/product-line.dto';

@Injectable()
export class ProductLineRestService {
  private productManagementUrl = 'api/productManagement';
  private getProductLinesUrl = `/api/productManagement/productlines`;
  private productLinesUrlPrefix = '/api/productManagement/clients';

  constructor(private httpClient: HttpClient) {}

  getList(
    pageQueries: PageQueriesModel
  ): Observable<PageDto<ProductLineDto>> {
    const url = `${this.productManagementUrl}/productlines`;
    const options = {
      params: pageQueries.getParams()
    };

    return this.httpClient.get(url, options).pipe(
      map(
        (productLinesPage: PageDto<ProductLineDto>) =>
          new PageDto(productLinesPage)
      ),
      catchError(this.handleError)
    );
  }

  getListByClientId(
    queries: PageQueriesModel,
    clientId: string
  ): Observable<PageDto<ProductLineDto>> {
    const options = {
      params: queries.getParams()
    };
    const url = `${this.productManagementUrl}/clients/${clientId}/productlines`;

    return this.httpClient.get(url, options).pipe(
      map((response: PageDto<ProductLineDto>) => new PageDto(response)),
      catchError(this.handleError)
    );
  }

  get(id: string): Observable<ProductLineDto> {
    return this.httpClient
      .get<ProductLineDto>(`${this.getProductLinesUrl}/${id}`)
      .pipe(
        take(1),
        map(productLine => new ProductLineDto(productLine)),
        catchError(this.handleError)
      );
  }

  create(
    productLine: ProductLineDto,
    clientId: string
  ): Observable<ProductLineDto> {
    const productLinesUrl = `${
      this.productLinesUrlPrefix
    }/${clientId}/productlines`;

    return this.httpClient
      .post<ProductLineDto>(productLinesUrl, productLine)
      .pipe(
        map(p => new ProductLineDto(p)),
        catchError(this.handleError)
      );
  }

  patch(
    id: string,
    productLine: ProductLineDto,
    clientId: string
  ): Observable<ProductLineDto> {
    const url = `${this.productLinesUrlPrefix}/${clientId}/productlines/${id}`;
    const options = { params: { clientId } };

    return this.httpClient.patch(url, productLine, options).pipe(
      map((res: ProductLineDto) => new ProductLineDto(res)),
      catchError(this.handleError)
    );
  }

  put(
    productLine: ProductLineDto,
    clientId: string
  ): Observable<ProductLineDto> {
    const id = productLine.identity.id;
    const productLinesUrl = `${
      this.productLinesUrlPrefix
    }/${clientId}/productlines/${id}`;

    return this.httpClient
      .put<ProductLineDto>(productLinesUrl, productLine)
      .pipe(
        map(p => new ProductLineDto(p)),
        catchError(this.handleError)
      );
  }

  delete(clientId: string, productLineId: string) {
    const productLinesUrl = `${
      this.productLinesUrlPrefix
    }/${clientId}/productlines/${productLineId}`;

    return this.httpClient.delete(productLinesUrl);
  }

  private handleError(errorResponse: any): Promise<any> {
    console.error('An error in ProductLineService: ', errorResponse);
    return Promise.reject(errorResponse.error.errors);
  }
}
